// One vocabulary for a bid's lifecycle, shared by the Bids list, Bid Details
// and the project pages. Mirrors the backend's allowed transitions
// (bid_managment/src/common/bid_status.py) — keep the two in step.
//
// Stored statuses: NEW, WIN, COMPLETED.
// EXPIRED is derived: a NEW bid whose last submission date has passed.
//
// WIN / COMPLETED belong to post-bid (a won bid becomes a project). While
// post-bid is off (config/features.js), actions leading there are not offered.

import { POST_BID_ENABLED } from '../config/features';

export const STATUS_LABEL = {
    NEW: 'Open',
    EXPIRED: 'Expired',
    WIN: 'Won',
    COMPLETED: 'Completed',
};

// Short explanation shown next to the status, so nobody has to guess.
export const STATUS_HINT = {
    NEW: 'Deadline ahead. Prepare and submit on the portal.',
    EXPIRED: 'The last submission date has passed.',
    WIN: 'Won. Project tracking is on.',
    COMPLETED: 'Project finished.',
};

// NEW past its deadline reads as EXPIRED; everything else is as stored.
export function effectiveStatus(bid) {
    const s = String((bid && bid.bid_status) || 'NEW').toUpperCase();
    if (s === 'NEW' && bid && bid.last_submission_date) {
        const due = new Date(bid.last_submission_date);
        if (!isNaN(due) && due.getTime() < Date.now()) return 'EXPIRED';
    }
    return s;
}

const CONFIRM_WON = {
    title: 'Mark This Bid as Won?',
    message: 'It moves to Active Projects and project tracking starts.',
    confirmLabel: 'Mark as Won',
};
const CONFIRM_COMPLETE = {
    title: 'Mark This Project as Completed?',
    message: 'It moves to Completed Projects. You can reopen it later.',
    confirmLabel: 'Mark as Completed',
};

// Actions offered for each (effective) status, primary first. `to` is the
// stored status to send; `confirm` (optional) is passed to confirmAction().
// Undo actions skip the confirmation: they are the way back.
// Open bids have none: they move to Expired on their own when the deadline
// passes, and a result is recorded only once it has.
export const STATUS_ACTIONS = {
    NEW: [],
    EXPIRED: [
        { to: 'WIN', label: 'Mark as Won', primary: true, confirm: CONFIRM_WON, success: 'Bid marked as won. It’s now in Active Projects.' },
    ],
    WIN: [
        { to: 'COMPLETED', label: 'Mark as Completed', primary: true, confirm: CONFIRM_COMPLETE, success: 'Project marked as completed' },
        { to: 'NEW', label: 'Undo Won', subtle: true, success: 'Bid moved back to Bids' },
    ],
    COMPLETED: [
        { to: 'WIN', label: 'Reopen Project', primary: true, success: 'Project reopened. It’s back in Active Projects.' },
    ],
};

const POST_BID_TARGETS = new Set(['WIN', 'COMPLETED']);

function available(actions) {
    return POST_BID_ENABLED ? actions : actions.filter(a => !POST_BID_TARGETS.has(a.to));
}

export function actionsFor(bid) {
    return available(STATUS_ACTIONS[effectiveStatus(bid)] || []);
}

// Same filter for callers that look actions up by status directly.
export function actionsForStatus(status) {
    return available(STATUS_ACTIONS[status] || []);
}

// Pre-bid statuses live under Bids; won and completed are projects.
export function isProjectStatus(status) {
    const s = String(status || '').toUpperCase();
    return s === 'WIN' || s === 'COMPLETED';
}
